import { get, set } from '@/helper/localStorage'
import _get from 'lodash/get'
import fields from '@/field/table/user'

export default {
  namespaced: true,
  state: {
    tableFields: get('userV2TableFields', fields),
    tableData: [],
    tablePagination: get('userV2TablePagination'),
    tableKeyword: get('userV2TableKeyword'),
    tableSort: get('userV2TableSort'),
    tableFilter: get('userV2TableFilter', []),
    detailData: get('userV2DetailData'),
    loadingTable: false,
    loadingDetail: false,
    loadingSubmit: false,
    infoSources: [],
    loadingInfoSources: false,
    registrationStatistic: [],
    loadingRegistrationStatistic: false,
    usersTotal: 0,
    loadingUsersTotal: false,
  },
  getters: {
    tableFields: state => {
      if (!state.tableFields) {
        return undefined
      }

      // only the visibility is customizable by the user
      const visibilities = state.tableFields.reduce(
        (obj, item) => Object.assign(obj, { [item.key]: item.visibility }), {},
      )

      // the other props uses the default fields configuration
      return fields.map(item => ({
        ...item,
        // only replace the visibility value if user override it
        visibility: item.key in visibilities ? visibilities[item.key] : item.visibility,
      }))
    },
    tableSort: state => {
      if (!state.tableSort) {
        return undefined
      }

      const symbol = state.tableSort.direction === 'desc' ? '-' : ''

      return `${symbol}${state.tableSort.key}`
    },
    tableData: state => {
      if (!state.tableData) {
        return []
      }

      return state.tableData.map(item => ({
        ...item,
        // add or replace field here
      }))
    },
    detailData: state => {
      if (!state.detailData) {
        return undefined
      }

      return {
        ...state.detailData,
        // add or replace field here
      }
    },
    tableFilter: state => {
      if (!state.tableFilter) {
        return []
      }

      return state.tableFilter
        .reduce((acc, elem) => Object.assign(acc, {
          [`filter[${elem.key}]`]: Array.isArray(elem.value)
            ? elem.value.join(',')
            : elem.value,
        }), {})
    },
    tableParams: (state, getters, rootState) => ({
      'filter[keyword]': state.tableKeyword,
      ...getters.tableFilter,
      sort: getters.tableSort,
      page: _get(state, 'tablePagination.current_page', 1),
      limit: rootState.appConfig.settings.tablePerPage,
    }),
  },
  mutations: {
    SET_TABLE_FIELDS(state, val) {
      set('userV2TableFields', val)
      state.tableFields = val
    },
    RESET_TABLE_FIELDS(state) {
      set('userV2TableFields', fields)
      state.tableFields = fields
    },
    SET_TABLE_DATA(state, val) {
      state.tableData = val
    },
    SET_TABLE_PAGINATION(state, val) {
      set('userV2TablePagination', val)
      state.tablePagination = val
    },
    SET_TABLE_KEYWORD(state, val) {
      set('userV2TableKeyword', val)
      state.tableKeyword = val
    },
    SET_DETAIL_DATA(state, val) {
      set('userV2DetailData', val)
      state.detailData = val
    },
    SET_TABLE_SORT(state, val) {
      set('userV2TableSort', val)
      state.tableSort = val
    },
    SET_TABLE_FILTER(state, val) {
      set('userV2TableFilter', val)
      state.tableFilter = val
    },
    SET_LOADING_TABLE(state, val) {
      state.loadingTable = val
    },
    SET_LOADING_DETAIL(state, val) {
      state.loadingDetail = val
    },
    SET_LOADING_SUBMIT(state, val) {
      state.loadingSubmit = val
    },
    SET_INFO_SOURCES(state, val) {
      state.infoSources = val
    },
    SET_LOADING_INFO_SOURCES(state, val) {
      state.loadingInfoSources = val
    },
    SET_REGISTRATION_STATISTIC(state, val) {
      state.registrationStatistic = val
    },
    SET_LOADING_REGISTRATION_STATISTIC(state, val) {
      state.loadingRegistrationStatistic = val
    },
    SET_USERS_TOTAL(state, val) {
      state.usersTotal = val
    },
    SET_LOADING_USERS_TOTAL(state, val) {
      state.loadingUsersTotal = val
    },
  },
  actions: {
    getTableData({ commit, getters }) {
      commit('SET_LOADING_TABLE', true)

      const { tableParams: params } = getters
      this._vm.$http.get('/v2/admin/users', { params })
        .then(({ data }) => {
          commit('SET_TABLE_DATA', data.data)
          commit('SET_TABLE_PAGINATION', data.meta)
        })
        .finally(() => {
          commit('SET_LOADING_TABLE', false)
        })
    },
    search(_, payload) {
      return this._vm.$http.get('/v2/admin/users?filter[type]=buyer', {
        params: {
          'filter[ids]': payload ? payload.ids : undefined,
          'filter[keyword]': payload ? payload.keyword : undefined,
        },
      })
        .then(({ data }) => data.data)
        .catch(() => [])
    },
    getDetail({ commit }, id) {
      commit('SET_DETAIL_DATA', null)
      commit('SET_LOADING_DETAIL', true)

      return this._vm.$http.get(`/v2/admin/users/${id}`)
        .then(({ data }) => {
          commit('SET_DETAIL_DATA', data.data)
        })
        .finally(() => {
          commit('SET_LOADING_DETAIL', false)
        })
    },
    getInfoSources({ commit }) {
      commit('SET_LOADING_INFO_SOURCES', true)
      return this._vm.$http.get('/v2/admin/info-sources-users')
        .then(({ data }) => {
          const rows = Array.isArray(data?.data) ? data.data : []
          commit('SET_INFO_SOURCES', rows)
          return rows
        })
        .finally(() => {
          commit('SET_LOADING_INFO_SOURCES', false)
        })
    },
    getRegistrationDiagram({ commit }, { startDate, endDate } = {}) {
      commit('SET_LOADING_REGISTRATION_STATISTIC', true)
      const params = {
        'filter[start_date]': startDate,
        'filter[end_date]': endDate,
      }
      return this._vm.$http.get('/v2/backoffice/diagram/user_registration', { params })
        .then(({ data }) => {
          const rows = Array.isArray(data?.data?.statistic) ? data.data.statistic : []
          commit('SET_REGISTRATION_STATISTIC', rows)
          return rows
        })
        .finally(() => {
          commit('SET_LOADING_REGISTRATION_STATISTIC', false)
        })
    },
    getUsersTotal({ commit }, { type = 'buyer' } = {}) {
      commit('SET_LOADING_USERS_TOTAL', true)
      const params = {
        'filter[type]': type,
        sort: '-created_at',
        page: 1,
        limit: 1,
      }
      return this._vm.$http.get('/v2/admin/users', { params })
        .then(({ data }) => {
          const total = data?.meta?.total || 0
          commit('SET_USERS_TOTAL', total)
          return total
        })
        .finally(() => {
          commit('SET_LOADING_USERS_TOTAL', false)
        })
    },
    exportUsers(_, { type = 'buyer' } = {}) {
      return this._vm.$http.get('/v2/admin/export/users', {
        params: { 'filter[type]': type },
      })
        .then(({ data }) => (data && data.data && Array.isArray(data.data.rows) ? data.data.rows : []))
        .catch(() => [])
    },
    update({ commit }, { id, data }) {
      commit('SET_LOADING_SUBMIT', true)
      return this._vm.$http.put(`/v1/admin/users/${id}`, data)
        .finally(() => {
          commit('SET_LOADING_SUBMIT', false)
        })
    },
  },
}
